/* Correction publiée d'un devoir, vue élève/responsable (bloc 07 → boucle). */
/* Chargée à l'ouverture ; rien n'est visible tant que le prof n'a pas publié. */

import { useState } from 'react';
import { Alert } from '@/components/ui/alert';
import { api } from '../lib/api';

interface Correction {
  total_score: number;
  total_max: number;
  overall_feedback: string;
  items: Array<{ section_title: string; score: number; max: number; feedback: string; letter?: string; letter_label?: string }>;
}

interface Graphie {
  pointPositif: string;
  conseils: Array<{ lettre: string | null; intitule: string; conseil: string }>;
}

export default function StudentCorrection({ studentId, assignmentId }: { studentId: string; assignmentId: string }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<Correction | null | undefined>(undefined);
  const [graphie, setGraphie] = useState<Graphie | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && data === undefined) {
      setLoading(true);
      setError(null);
      try {
        const r = await api.parentOrTutor.student.correction.query({ studentId, assignmentId });
        setData(r as Correction | null);
        const g = await api.parentOrTutor.student.graphie.query({ studentId, assignmentId }).catch(() => null);
        setGraphie(g as Graphie | null);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="w-full mt-[6px]">
      <button type="button" className="inline-flex items-center gap-[6px] min-h-[40px] px-1 border-none bg-transparent text-primary text-[13px] font-bold cursor-pointer" onClick={() => void toggle()} aria-expanded={open}>
        {open ? '▾' : '▸'} Ma correction
      </button>

      {open && (
        <>
          {loading && <p className="text-muted-foreground text-[13px] my-[6px]">Chargement…</p>}
          {error && <Alert>{error}</Alert>}
          {graphie && (
            <div className="mt-2 p-[14px] border border-border rounded-card bg-card flex flex-col gap-2" data-testid="student-graphie">
              <div className="text-[13.5px] font-bold text-foreground">Conseils d’écriture</div>
              {graphie.pointPositif && <p className="m-0 text-[13px] leading-[1.45] text-foreground">{graphie.pointPositif}</p>}
              <ul className="m-0 pl-5 flex flex-col gap-1">
                {graphie.conseils.map((c, i) => (
                  <li key={i} className="text-[13px] leading-[1.45] text-foreground">{c.conseil}</li>
                ))}
              </ul>
            </div>
          )}
          {data === null && !graphie && !loading && <p className="text-muted-foreground text-[13px] my-[6px]">Pas encore corrigée par le professeur.</p>}
          {data && (
            <div className="mt-2 p-[14px] border border-border rounded-card bg-card flex flex-col gap-3">
              {data.total_max > 0 && (
                <div className="flex items-baseline gap-1">
                  <span className="text-[32px] font-extrabold leading-none text-foreground [font-variant-numeric:tabular-nums]">{data.total_score}</span>
                  <span className="text-[15px] font-semibold text-muted-foreground">/ {data.total_max}</span>
                </div>
              )}
              {data.overall_feedback && (
                <p className="m-0 p-[10px_12px] rounded-[10px] bg-[var(--naia-bg,rgba(11,42,74,0.04))] text-[13.5px] leading-relaxed text-foreground">{data.overall_feedback}</p>
              )}
              {data.items.length > 0 && (
                <div className="flex flex-col gap-[10px]">
                  {data.items.map((it, i) => (
                    <div key={i} className="pb-[10px] border-b border-border last:border-b-0 last:pb-0">
                      <div className="flex items-baseline justify-between gap-[10px]">
                        <span className="text-[13.5px] font-bold text-foreground [overflow-wrap:anywhere]">{it.section_title || `Partie ${i + 1}`}</span>
                        <span className="flex-none text-[14px] font-bold text-foreground [font-variant-numeric:tabular-nums]">{it.letter ? `${it.letter}${it.letter_label ? ` — ${it.letter_label}` : ''}` : `${it.score}/${it.max}`}</span>
                      </div>
                      {it.feedback && <p className="mt-1 m-0 text-[13px] leading-[1.45] text-muted-foreground">{it.feedback}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
